import { useEffect, useState } from 'react';
import { Box, Card, CardContent, CircularProgress, Typography } from '@mui/material';
import LoginIcon from '@mui/icons-material/Login';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useFilterContext } from '../../usageandbilling/FilterContext';
import { fetchLoginsSummary } from '../../services/dashboard.service';

interface LoginsSummaryResponse {
    totalLogins: number;
    totalHours: number;
    breakdownByOrg: Array<{ orgId: string; orgName: string; sessions: number; hours: number }>;
}

const TotalLoginsAndHours = () => {
    const { selectedOrgIds, selectedUserIds, dateRange } = useFilterContext();

    const [data, setData] = useState<LoginsSummaryResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const controller = new AbortController();

        const fetchSummary = async () => {
            setLoading(true);
            setError(null);

            try {
                const params = new URLSearchParams();
                if (selectedOrgIds.length > 0) params.set('orgIds', selectedOrgIds.join(','));
                if (selectedUserIds.length > 0) params.set('userIds', selectedUserIds.join(','));
                params.set('startDate', dateRange[0].format('YYYY-MM-DD'));
                params.set('endDate', dateRange[1].format('YYYY-MM-DD'));

                const res = await fetchLoginsSummary(params.toString(), controller.signal);

                setData(res as LoginsSummaryResponse);
            } catch (err: unknown) {
                if ((err as { name?: string }).name === 'AbortError') return;
                setError('Failed to load logins summary.');
            } finally {
                setLoading(false);
            }
        };

        fetchSummary();

        return () => {
            controller.abort();
        };
    }, [selectedOrgIds, selectedUserIds, dateRange]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={100}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Typography color="error" sx={{ py: 2 }}>
                {error}
            </Typography>
        );
    }

    return (
        <Box display="flex" gap={2}>
            {/* Total Logins */}
            <Card sx={{ flex: 1, p: 2 }}>
                <CardContent>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <LoginIcon color="primary" />
                        <Typography variant="subtitle2" color="text.secondary">
                            Total Logins
                        </Typography>
                    </Box>
                    <Typography variant="h4" fontWeight="bold">
                        {data ? data.totalLogins.toLocaleString() : '—'}
                    </Typography>
                </CardContent>
            </Card>

            {/* Total Hours */}
            <Card sx={{ flex: 1, p: 2 }}>
                <CardContent>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <AccessTimeIcon color="secondary" />
                        <Typography variant="subtitle2" color="text.secondary">
                            Total Hours
                        </Typography>
                    </Box>
                    <Typography variant="h4" fontWeight="bold">
                        {data ? data.totalHours.toFixed(1) : '—'}
                    </Typography>
                </CardContent>
            </Card>
        </Box>
    );
};

export default TotalLoginsAndHours;
